import { ErrorFallback, errorHandler } from '@dxb-web-ui-framework/common/components/ErrorFallback/ErrorFallback';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import React, { forwardRef, PropsWithChildren, useImperativeHandle } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import {
  ClientSideWizardCurrentProcessState,
  ClientSideWizardStateHandlerProps,
  PreloadedFormDataConfig,
  WizardStepperTypeEnum,
} from '../../contexts/ClientSideWizard/types';
import {
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
  useWizardState,
} from '../../contexts/ClientSideWizard/hooks';
import { defaultClientSideWizardStateHandler } from '../../contexts/ClientSideWizard/defaultClientSideWizardStateHandler';
import { ClientSideWizardProvider } from '../../contexts/ClientSideWizard/ClientSideWizardProvider';
import { ClientSideWizard } from './ClientSideWizard';

export type ClientSideWizardFlowProps = {
  stepsConfig: GenericObject;
  processName: string;
  stepperConfig?: GenericObject[];
  preloadedFormDataConfig?: PreloadedFormDataConfig;
  isTakeover?: Boolean;
  hasOverflowYAuto?: Boolean;
  preloadedDataAdapter?: Function;
  stateHandlerOverride?: (props: ClientSideWizardStateHandlerProps) => void;
  ErrorFallbackOverride?: React.ComponentType<FallbackProps>;
  errorHandlerOverride?: (error: any) => void;
  stepperType?: WizardStepperTypeEnum;
  hasProgressbar?: boolean;
  externalEffectCallbacks?: GenericObject;
  onStepChange?: (completedSteps: number, totalSteps: number) => void;
  onSubmit?: (currentProcessState?: ClientSideWizardCurrentProcessState) => Promise<void> | void | undefined;
  onSuccess?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
  onProcessEnd?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
  onCancel?: React.MouseEventHandler;
  onPreviousFirstStep?: (currentProcessState?: ClientSideWizardCurrentProcessState) => void | undefined;
  shouldEnablePaddingBottom?: boolean;
  rootContainerClasses?: string;
  childContainerClasses?: string;
};

const ClientSideWizardFlowPresenter = forwardRef<HTMLFormElement, PropsWithChildren<ClientSideWizardFlowProps>>(
  (
    {
      stepperConfig,
      isTakeover,
      hasOverflowYAuto,
      stepsConfig,
      processName,
      stateHandlerOverride,
      ErrorFallbackOverride,
      errorHandlerOverride,
      stepperType,
      hasProgressbar,
      onSuccess,
      onProcessEnd,
      onCancel,
      onSubmit,
      onStepChange,
      preloadedFormDataConfig,
      onPreviousFirstStep,
      externalEffectCallbacks,
      shouldEnablePaddingBottom,
      rootContainerClasses,
      childContainerClasses,
    },
    ref,
  ) => {
    const wizardState = useWizardState();
    const goToNextStep = useWizardGoToNextStep();
    const goToPreviousStep = useWizardGoToPreviousStep();

    const stateHandler =
      stateHandlerOverride && typeof stateHandlerOverride === 'function'
        ? stateHandlerOverride
        : defaultClientSideWizardStateHandler;
    const onError =
      errorHandlerOverride && typeof errorHandlerOverride === 'function' ? errorHandlerOverride : errorHandler;
    const FallbackComponent = ErrorFallbackOverride || ErrorFallback;

    useImperativeHandle(
      ref,
      (): any => ({
        wizardControls: {
          goToPreviousStep,
          goToNextStep,
        },
        wizardState,
      }),
      [goToPreviousStep, goToNextStep, wizardState],
    );

    return (
      <ErrorBoundary
        FallbackComponent={FallbackComponent}
        onError={onError}
        onReset={(e: any) => onCancel && onCancel(e)}>
        <ClientSideWizardProvider
          stepsConfig={stepsConfig}
          processName={processName}
          stateHandler={stateHandler as any}
          onSuccess={onSuccess}
          preloadedFormDataConfig={preloadedFormDataConfig}
          onProcessEnd={onProcessEnd}
          onCancel={onCancel}
          onSubmit={onSubmit}
          onStepChange={onStepChange}
          onPreviousFirstStep={onPreviousFirstStep}>
          <ClientSideWizard
            stepperConfig={stepperConfig}
            onCancel={onCancel}
            onSubmit={onSubmit}
            stepperType={stepperType}
            hasProgressbar={hasProgressbar}
            isTakeover={isTakeover}
            hasOverflowYAuto={hasOverflowYAuto}
            shouldEnablePaddingBottom={shouldEnablePaddingBottom}
            externalEffectCallbacks={externalEffectCallbacks}
            rootContainerClasses={rootContainerClasses}
            childContainerClasses={childContainerClasses}
          />
        </ClientSideWizardProvider>
      </ErrorBoundary>
    );
  },
);

export { ClientSideWizardFlowPresenter };
